/**
 * RentFastSection — homepage "Rent your unit out fast" module.
 *
 * Positioned right after the hero on Landing.jsx so any landlord who
 * lands on centralrentals.ca sees an immediate 3-step promise + real
 * KPIs. Every card links to the relevant in-app flow so the visitor
 * can jump straight into the funnel.
 *
 * SEO: emits a `HowTo` JSON-LD block so Google can surface this as a
 * "how to rent out a unit" rich result and pull Central Rentals into
 * the answer box.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Rocket, UserCheck, Handshake, ArrowRight, Zap, Clock, Users } from "lucide-react";

const STEPS = [
  {
    n: 1,
    icon: Rocket,
    kicker: "Step 1 · List",
    title: "Publish a smart listing in under 90 seconds",
    body: "Photos, address, and rent price → we auto-generate a Canadian-compliant listing, syndicate it to Kijiji + Facebook Marketplace, and start collecting rental applications the same day.",
    cta: { label: "Create a listing", to: "/signup?role=landlord&flow=list" },
    testid: "rentfast-step-list",
    // Static Tailwind classes so JIT safelists them at build time.
    // Solid saturated fills (bg-*-600 · text-white) so the icons pop
    // against the section's plain-white background.
    iconBg: "bg-emerald-600 text-white",
  },
  {
    n: 2,
    icon: UserCheck,
    kicker: "Step 2 · Screen",
    title: "Screen applicants automatically",
    body: "Credit + ID + employment + reference check in one flow (Certn + Equifax). Get a rank-ordered short-list in under 24 hours — no back-and-forth spreadsheets.",
    cta: { label: "See how screening works", to: "/screening" },
    testid: "rentfast-step-screen",
    iconBg: "bg-sky-600 text-white",
  },
  {
    n: 3,
    icon: Handshake,
    kicker: "Step 3 · Move-in",
    title: "Sign the lease and collect first rent",
    body: "Province-compliant lease generated from your template, e-signed by both parties, first-month rent collected via Stripe in CAD, and keys handed over. The whole flow lives in one dashboard.",
    cta: { label: "Try a lease template", to: "/leases" },
    testid: "rentfast-step-signing",
    iconBg: "bg-violet-600 text-white",
  },
];

const KPIS = [
  { icon: Clock, label: "Median days to sign a lease", value: "9", unit: "days", testid: "kpi-days" },
  { icon: Users, label: "Applications per listing (Canadian avg)", value: "14", unit: "applicants", testid: "kpi-apps" },
  { icon: Zap, label: "Screening turnaround", value: "24", unit: "hrs", testid: "kpi-screen" },
];

export default function RentFastSection() {
  // Inject HowTo JSON-LD — Google may surface the steps as a rich
  // result under "How to rent out a unit in Canada". Removed on
  // unmount so route changes don't leak duplicated schema.
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-jsonld", "rent-fast-howto");
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to rent out your unit fast in Canada",
      "description":
        "A three-step process to list, screen, and sign a new tenant using Central Rentals Canada — median 9 days from listing to signed lease.",
      "totalTime": "P9D",
      "estimatedCost": { "@type": "MonetaryAmount", "currency": "CAD", "value": "5" },
      "supply": [
        { "@type": "HowToSupply", "name": "Property photos" },
        { "@type": "HowToSupply", "name": "Monthly rent price" },
        { "@type": "HowToSupply", "name": "Property address" },
      ],
      "tool": [
        { "@type": "HowToTool", "name": "Central Rentals Canada dashboard" },
      ],
      "step": STEPS.map((s) => ({
        "@type": "HowToStep",
        "position": s.n,
        "name": s.title,
        "text": s.body,
        "url": `https://centralrentals.ca${s.cta.to}`,
      })),
    });
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, []);

  return (
    <section
      id="rent-your-unit-fast"
      data-testid="rent-fast-section"
      className="relative py-16 md:py-24 bg-white border-y border-slate-100 overflow-hidden"
    >
      {/* Subtle decorative glow (barely-there, keeps things simple) */}
      <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-emerald-100 blur-[120px]"/>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-sky-100 blur-[120px]"/>
      </div>

      <div className="relative max-w-6xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-700">
            <Zap className="w-3.5 h-3.5"/> Rent your unit out fast
          </div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            List, screen, and sign a new tenant in under 10 days
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Canadian landlords using Central Rentals sign their next tenant a median of <b className="text-emerald-700">9 days</b> after publishing the listing. Here&apos;s the three-step process — every step lives inside your dashboard.
          </p>
        </div>

        {/* KPI band */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {KPIS.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.testid} data-testid={k.testid} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
                <Icon className="w-6 h-6 text-emerald-600 mx-auto mb-2"/>
                <div className="font-display text-3xl font-extrabold text-slate-900 tabular-nums">
                  {k.value}<span className="text-sm text-slate-500 ml-1">{k.unit}</span>
                </div>
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mt-1">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* 3-step grid */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.testid} data-testid={s.testid} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-emerald-400 hover:shadow-md transition-all flex flex-col">
                <div className={`w-12 h-12 rounded-xl ${s.iconBg} flex items-center justify-center`}>
                  <Icon className="w-6 h-6"/>
                </div>
                <div className="mt-4 text-[11px] uppercase tracking-widest font-bold text-emerald-700">{s.kicker}</div>
                <h3 className="mt-1 font-display text-lg sm:text-xl font-extrabold text-slate-900 leading-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{s.body}</p>
                <Link
                  to={s.cta.to}
                  data-testid={`${s.testid}-cta`}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-extrabold text-emerald-700 hover:text-emerald-900 group"
                >
                  {s.cta.label}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"/>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <Link
            to="/signup?role=landlord&plan=starter"
            data-testid="rent-fast-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-base font-extrabold shadow-lg shadow-emerald-600/20 transition-colors"
          >
            Start listing from CA$35/month
            <ArrowRight className="w-5 h-5"/>
          </Link>
          <p className="mt-3 text-xs text-slate-500">Cancel anytime · No long-term contract</p>
        </div>
      </div>
    </section>
  );
}
